import { ThemeEvents } from '@theme/events';

class SizeCheckPopup extends HTMLElement {
  #dialog;
  #trigger;
  #closeButton;
  #returnFocus;
  #successTimer;
  #fadeTimer;

  #handleDocumentChange = () => {
    this.#updateCapSize();
  };

  #handleVariantUpdate = () => {
    requestAnimationFrame(() => this.#updateCapSize());
  };

  #handleKeydown = (event) => {
    if (!this.#dialog?.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.#getFocusableElements();
    if (focusable.length === 0) {
      event.preventDefault();
      this.#dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #handleBackdropClick = (event) => {
    if (event.target !== this.#dialog) return;

    const rect = this.#dialog.getBoundingClientRect();
    const isOutside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;

    if (isOutside) this.#close();
  };

  #handleClose = () => {
    document.documentElement.classList.remove('size-check-popup-open');

    if (this.querySelector('[data-size-check-success]')) {
      this.#resetSuccessfulSubmission();
    }

    if (this.#returnFocus instanceof HTMLElement && this.#returnFocus.isConnected) {
      this.#returnFocus.focus();
    }
  };

  connectedCallback() {
    this.#dialog = this.querySelector('[data-size-check-dialog]');
    this.#trigger = this.querySelector('[data-size-check-trigger]');
    this.#closeButton = this.querySelector('[data-size-check-close]');

    if (!(this.#dialog instanceof HTMLDialogElement) || !(this.#trigger instanceof HTMLElement)) return;

    this.#trigger.addEventListener('click', () => {
      this.#returnFocus = this.#trigger;
      this.#open();
    });
    this.#closeButton?.addEventListener('click', () => this.#close());
    this.#dialog.addEventListener('click', this.#handleBackdropClick);
    this.#dialog.addEventListener('keydown', this.#handleKeydown);
    this.#dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#close();
    });
    this.#dialog.addEventListener('close', this.#handleClose);

    document.addEventListener('change', this.#handleDocumentChange);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    this.#updateCapSize();

    const success = this.querySelector('[data-size-check-success]');
    if (success) {
      this.#returnFocus = this.#trigger;
      this.#open();
      this.#scheduleSuccessClose(success);
    } else if (this.querySelector('[data-size-check-errors]')) {
      this.#open();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#handleDocumentChange);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.documentElement.classList.remove('size-check-popup-open');
    this.#clearSuccessTimers();
  }

  #open() {
    if (this.#dialog.open) return;

    if (!(this.#returnFocus instanceof HTMLElement) || !this.#returnFocus.isConnected) {
      this.#returnFocus = document.activeElement;
    }

    this.#updateCapSize();
    document.documentElement.classList.add('size-check-popup-open');
    this.#dialog.showModal();

    requestAnimationFrame(() => {
      const priorityTarget =
        this.querySelector('[data-size-check-success], [data-size-check-errors]') ||
        this.#dialog.querySelector('input:not([type="hidden"]), textarea, button');

      priorityTarget?.focus();
    });
  }

  #close() {
    if (this.#dialog?.open) this.#dialog.close();
  }

  #scheduleSuccessClose(success) {
    this.#clearSuccessTimers();

    this.#successTimer = window.setTimeout(() => {
      success.classList.add('is-fading');

      this.#fadeTimer = window.setTimeout(() => {
        this.#close();
      }, 250);
    }, 4750);
  }

  #clearSuccessTimers() {
    window.clearTimeout(this.#successTimer);
    window.clearTimeout(this.#fadeTimer);
    this.#successTimer = undefined;
    this.#fadeTimer = undefined;
  }

  #resetSuccessfulSubmission() {
    this.#clearSuccessTimers();

    const success = this.querySelector('[data-size-check-success]');
    const fields = this.querySelector('[data-size-check-form-fields]');
    const form = this.querySelector('form');

    if (form instanceof HTMLFormElement) form.reset();

    if (success instanceof HTMLElement) {
      success.hidden = true;
      success.classList.remove('is-fading');
    }

    if (fields instanceof HTMLElement) fields.hidden = false;

    this.#updateCapSize();

    const url = new URL(window.location.href);
    url.searchParams.delete('contact_posted');
    if (url.hash.includes('contact_form')) url.hash = '';
    window.history.replaceState({}, '', url);
  }

  #getFocusableElements() {
    return Array.from(
      this.#dialog.querySelectorAll(
        'button:not([disabled]), input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href]'
      )
    ).filter((element) => element instanceof HTMLElement && !element.hidden);
  }

  #updateCapSize() {
    const hiddenInput = this.querySelector('[data-size-check-cap-size]');
    if (!(hiddenInput instanceof HTMLInputElement)) return;

    const fieldset = Array.from(document.querySelectorAll('fieldset')).find((element) => {
      const legend = element.querySelector('legend');
      return legend?.textContent?.trim().toLowerCase() === 'cap size';
    });

    const checkedInput = fieldset?.querySelector('input:checked');
    const selectedValue =
      checkedInput?.labels?.[0]?.textContent?.trim() ||
      checkedInput?.value ||
      Array.from(document.querySelectorAll('select')).find((select) =>
        Array.from(select.labels || []).some(
          (label) => label.textContent?.trim().toLowerCase() === 'cap size'
        )
      )?.value ||
      '';

    hiddenInput.value = selectedValue;
  }
}

if (!customElements.get('size-check-popup')) {
  customElements.define('size-check-popup', SizeCheckPopup);
}
